import type { Bundle } from '@/api/bundles/bundles-types'
import type { Order } from '@/api/orders/orders-types'

/**
 * A bundle is locked against scan-driven (re)assignment only when it is complete
 * AND actually on a skid. `is_complete` with a null `skid_id` is invalid/drifted
 * data — the app blocks unassigning a complete bundle, so that pairing only comes
 * from backend/legacy data. Treating it as locked dead-ends the operator with an
 * "uncheck Complete to change the shipping unit" message when there is no shipping
 * unit to change, so let the scan assign one instead.
 */
export function isBundleLockedForScan(
  bundle: Pick<Bundle, 'is_complete' | 'skid_id'>
): boolean {
  return bundle.is_complete === true && bundle.skid_id != null
}

/** Counts bundles and how many have a skid assigned (`skid_id` set). */
export function getBundlePackagingProgress(order: Order): { assigned: number; total: number } {
  let total = 0
  let assigned = 0
  for (const d of order.details) {
    for (const b of d.bundles ?? []) {
      total += 1
      if (b.skid_id != null) assigned += 1
    }
  }
  return { total, assigned }
}

/**
 * Bundles alone in a Box unit — the backend's one-per-bundle boxes, which it keeps when the
 * order's unit type changes. Read from the order's own bundles and `skids` labels.
 */
export function countBundlesInOwnBox(order: Order): number {
  const boxIds = new Set((order.skids ?? []).filter((s) => s.label === 'Box').map((s) => s.id))
  const perBox = new Map<number, number>()
  for (const d of order.details) {
    for (const b of d.bundles ?? []) {
      if (b.skid_id != null && boxIds.has(b.skid_id)) perBox.set(b.skid_id, (perBox.get(b.skid_id) ?? 0) + 1)
    }
  }
  return [...perBox.values()].filter((count) => count === 1).length
}

export function isAllBundlesOnSkids(order: Order): boolean {
  const { total, assigned } = getBundlePackagingProgress(order)
  return total > 0 && assigned === total
}
